import React, { FC, ReactNode } from "react";
import classnames from "classnames";
import styles from "./PackageCard.module.css";
import { ChevronRight, CircleCheckmarkSolid, ShieldCheck } from "../../icons";

type UspIcon = "checkmark-circle" | "checkmark-shield";

/* The two glyphs a usp is allowed to carry. Kept as a lookup rather than a
 * prop so callers keep passing the same string they always did. */
const USP_ICONS: Record<UspIcon, ReactNode> = {
  "checkmark-circle": <CircleCheckmarkSolid size={16} />,
  "checkmark-shield": <ShieldCheck size={16} />,
};

export type PackageCardProps = {
  name: string;
  description: string;
  iconSrc: string;
  popular?: boolean;
  popularLabel?: string;
  coverageLabel: string;
  coverageValue: ReactNode;
  // Reserve space for a second coverage line so bullets align across a set of
  // cards. Set when at least one card in the set has the subtext line.
  reserveCoverageSubtext?: boolean;
  usps: { text: string; icon: UspIcon }[];
  gradient?: { from: string; to: string };
  readMoreLabel?: string;
  onReadMore?: () => void;
  showCta?: boolean;
  ctaLabel?: string;
  onCtaClick?: () => void;
  className?: string;
};

export const PackageCard: FC<PackageCardProps> = ({
  name,
  description,
  iconSrc,
  popular,
  popularLabel,
  coverageLabel,
  coverageValue,
  reserveCoverageSubtext,
  usps,
  gradient,
  readMoreLabel,
  onReadMore,
  showCta,
  ctaLabel,
  onCtaClick,
  className,
}) => {
  const washStyle = gradient
    ? {
        background: `linear-gradient(194deg, ${gradient.to} 12%, ${gradient.from} 63%)`,
      }
    : undefined;

  return (
    <div className={classnames(styles.card, className)}>
      <div className={styles.wash} style={washStyle} />
      {popular && popularLabel && (
        <span className={styles.popularPill}>{popularLabel}</span>
      )}
      <div className={styles.header}>
        <img src={iconSrc} alt="" className={styles.iconImg} />
        <div className={styles.headerText}>
          <p className={styles.name}>{name}</p>
          <p className={styles.description}>{description}</p>
        </div>
      </div>
      <div className={styles.body}>
        <p className={styles.coverageLabel}>{coverageLabel}</p>
        <p
          className={classnames(styles.coverageValue, {
            [styles.coverageValueReserve]: reserveCoverageSubtext,
          })}
        >
          {coverageValue}
        </p>
        <ul className={styles.usps}>
          {usps.map((usp) => (
            <li key={usp.text} className={styles.uspItem}>
              <span className={styles.uspIcon}>{USP_ICONS[usp.icon]}</span>
              {usp.text}
            </li>
          ))}
        </ul>
        <div className={styles.footer}>
          {readMoreLabel && onReadMore && (
            <button
              type="button"
              className={styles.readMore}
              onClick={onReadMore}
            >
              {readMoreLabel}
              <ChevronRight size={16} />
            </button>
          )}
          {showCta && ctaLabel && (
            <button
              type="button"
              className={styles.ctaButton}
              onClick={onCtaClick}
            >
              {ctaLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
